/**
 * Consultas del Portal Condominio que arma el servidor (migraciones 20261015210100 y siguientes).
 * Reemplazan, menú por menú, a `fetchCondominioDataset`, que armaba todo en el navegador con ~80
 * peticiones en serie y sin filtrar acuerdos inactivos. El detalle de una cuenta es el de Cobranza
 * (`CobranzaCuentaDetalle`), así que no tiene consulta propia aquí.
 */
import { supabase } from "@/integrations/supabase/client";
import { esRpcInexistente } from "@/lib/rpcErrors";
import { normalizarUnidad, type TendenciaCondominio, type UnidadCondominioRpc } from "./unidades";
import type { CondominioRef } from "@/types/condominio";
import { hoyCdmx, normalizarCargo, normalizarPago, type CargoCobranza, type PagoCobranza } from "./cobranza";
import { normalizarMesTesoreria, type MesTesoreria } from "./tesoreria";
import { normalizarEvento, type EventoBitacora } from "./auditoria";
import {
  normalizarAgenda, normalizarAmenidad, normalizarConfig, normalizarDetalle, normalizarDisponibilidad, normalizarEspacio,
  normalizarIncidencia, normalizarMetricas, normalizarPagoReserva, normalizarReporte, normalizarReserva, normalizarReservas,
  normalizarTitulares, sumarDias, type AgendaAmen, type AmenidadAmen, type ConfigAmen, type DetalleReservaAmen,
  type DisponibilidadAmen, type EfectoCancelacion, type EfectoCancelacionInfo, type EspacioAmen, type EstadoPublicacion,
  type IncidenciaAmen, type MediaAmen, type MetricasAmen, type PagoReserva, type ReporteAmen, type ReservaFila,
  type ReservasAmen, type TitularesCuenta,
} from "./amenidades";

/** La RPC aún no está en este ambiente: la pantalla lo dice en vez de mostrar ceros. */
export class RpcCondominioPendiente extends Error {
  constructor() {
    super("Se activa cuando se aplique la migración del Portal Condominio.");
  }
}

const sb = supabase as any;

/**
 * "Ver como" del Portal Condominio: con `verComo` (auth_user_id del impersonado) el servidor
 * calcula permiso y alcance con SU identidad (20261022095845; solo si el usuario real puede
 * impersonar). Sin él, con la sesión. Solo lecturas: las escrituras nunca lo mandan.
 */
const conVerComo = <T extends Record<string, unknown>>(args: T, verComo?: string | null) =>
  verComo ? { ...args, p_auth_user_id: verComo } : args;

/** `null` si la RPC aún no existe (el llamador usa su consulta anterior). */
export async function fetchCondominiosRpc(verComo?: string | null): Promise<CondominioRef[] | null> {
  const { data, error } = await sb.rpc("get_condominios", conVerComo({}, verComo));
  if (error) {
    if (esRpcInexistente(error)) return null;
    throw error;
  }
  return ((data as { id: number; nombre: string }[]) ?? []).map((p) => ({ id: Number(p.id), nombre: p.nombre }));
}

export async function fetchUnidadesCondominio(proyectoId: number, verComo?: string | null): Promise<UnidadCondominioRpc[]> {
  const { data, error } = await sb.rpc("get_condominio_unidades", conVerComo({ p_id_proyecto: proyectoId }, verComo));
  if (error) {
    if (esRpcInexistente(error)) throw new RpcCondominioPendiente();
    throw error;
  }
  return ((data as Record<string, unknown>[]) ?? []).map(normalizarUnidad);
}

export async function fetchTendenciaCondominio(proyectoId: number, meses = 12, verComo?: string | null): Promise<TendenciaCondominio[]> {
  const { data, error } = await sb.rpc("get_condominio_tendencia", conVerComo({ p_id_proyecto: proyectoId, p_meses: meses }, verComo));
  if (error) {
    if (esRpcInexistente(error)) throw new RpcCondominioPendiente();
    throw error;
  }
  return ((data as Record<string, unknown>[]) ?? []).map((f) => ({
    mes: String(f.mes).slice(0, 10),
    mtto_esperado: Number(f.mtto_esperado) || 0,
    fondo_esperado: Number(f.fondo_esperado) || 0,
    cobrado: Number(f.cobrado) || 0,
  }));
}

/** Pagos de las cuentas de mantenimiento en el periodo, con su conciliación (20261015230000). */
export async function fetchPagosCondominio(proyectoId: number, desde: string | null, hasta: string | null, verComo?: string | null): Promise<PagoCobranza[]> {
  const { data, error } = await sb.rpc("get_condominio_pagos", conVerComo({ p_id_proyecto: proyectoId, p_desde: desde, p_hasta: hasta }, verComo));
  if (error) {
    if (esRpcInexistente(error)) throw new RpcCondominioPendiente();
    throw error;
  }
  return ((data as Record<string, unknown>[]) ?? []).map(normalizarPago);
}

/** Cuotas de mantenimiento, fondo de reserva y multas del periodo con su estatus (20261015230000). */
export async function fetchCargosCondominio(proyectoId: number, desde: string | null, hasta: string | null, verComo?: string | null): Promise<CargoCobranza[]> {
  const { data, error } = await sb.rpc("get_condominio_cargos", conVerComo({ p_id_proyecto: proyectoId, p_desde: desde, p_hasta: hasta }, verComo));
  if (error) {
    if (esRpcInexistente(error)) throw new RpcCondominioPendiente();
    throw error;
  }
  return ((data as Record<string, unknown>[]) ?? []).map(normalizarCargo);
}

/** Ingresos por mes de las cuentas de mantenimiento y su aplicación (20261016000000). */
export async function fetchTesoreriaCondominio(proyectoId: number, meses: number, verComo?: string | null): Promise<MesTesoreria[]> {
  const { data, error } = await sb.rpc("get_condominio_tesoreria", conVerComo({ p_id_proyecto: proyectoId, p_meses: meses }, verComo));
  if (error) {
    if (esRpcInexistente(error)) throw new RpcCondominioPendiente();
    throw error;
  }
  return ((data as Record<string, unknown>[]) ?? []).map(normalizarMesTesoreria);
}

/** Bitácora de eventos reales del condominio en el rango (20261016010000). */
export async function fetchBitacoraCondominio(proyectoId: number, desde: string | null, hasta: string | null, verComo?: string | null): Promise<EventoBitacora[]> {
  const { data, error } = await sb.rpc("get_condominio_bitacora", conVerComo({ p_id_proyecto: proyectoId, p_desde: desde, p_hasta: hasta }, verComo));
  if (error) {
    if (esRpcInexistente(error)) throw new RpcCondominioPendiente();
    throw error;
  }
  return ((data as Record<string, unknown>[]) ?? []).map(normalizarEvento);
}

// ---------------------------------------------------------------------------------------------
// Amenidades (Ticket #1522). Contrato de las RPC `amen_*` y de la edge `admin-amenidades` en el
// diseño del módulo; la lógica pura y los tipos viven en `./amenidades`. Lecturas con "Ver como";
// las escrituras nunca lo mandan. Sin la RPC en el ambiente: `RpcCondominioPendiente`.
// ---------------------------------------------------------------------------------------------

async function rpcAmen(fn: string, args: Record<string, unknown>): Promise<unknown> {
  const { data, error } = await sb.rpc(fn, args);
  if (error) {
    if (esRpcInexistente(error)) throw new RpcCondominioPendiente();
    throw error;
  }
  return data;
}

type Crudo = Record<string, unknown>;

export async function fetchAmenConfig(idProyecto: number, verComo?: string | null): Promise<ConfigAmen> {
  const d = (await rpcAmen("amen_config", conVerComo({ p_id_proyecto: idProyecto }, verComo))) as Crudo | null;
  return normalizarConfig(d ?? {}, idProyecto);
}

export async function guardarAmenConfig(idProyecto: number, p: Partial<ConfigAmen>): Promise<ConfigAmen> {
  const d = (await rpcAmen("amen_guardar_config", { p_id_proyecto: idProyecto, p })) as Crudo | null;
  return normalizarConfig(d ?? {}, idProyecto);
}

export async function fetchAmenCatalogo(idProyecto: number, verComo?: string | null): Promise<AmenidadAmen[]> {
  const d = await rpcAmen("amen_catalogo", conVerComo({ p_id_proyecto: idProyecto }, verComo));
  return ((d as Crudo[]) ?? []).map(normalizarAmenidad).sort((a, b) => a.orden - b.orden || a.nombre.localeCompare(b.nombre));
}

export async function guardarAmenidad(p: Crudo): Promise<AmenidadAmen> {
  return normalizarAmenidad(((await rpcAmen("amen_guardar_amenidad", { p })) as Crudo) ?? {});
}

export async function guardarEspacioAmen(p: Crudo): Promise<EspacioAmen> {
  return normalizarEspacio(((await rpcAmen("amen_guardar_espacio", { p })) as Crudo) ?? {});
}

export async function guardarReglamentoAmen(idAmenidad: number, texto: string, storagePath: string | null = null): Promise<{ id: number; version: number }> {
  const d = ((await rpcAmen("amen_guardar_reglamento", { p_id_amenidad: idAmenidad, p_texto: texto, p_storage_path: storagePath })) as Crudo) ?? {};
  return { id: Number(d.id) || 0, version: Number(d.version) || 1 };
}

export async function guardarMediaAmen(idAmenidad: number, items: Omit<MediaAmen, "id">[]): Promise<void> {
  await rpcAmen("amen_guardar_media", { p_id_amenidad: idAmenidad, p_items: items });
}

export async function cambiarPublicacionAmen(idAmenidad: number, estado: EstadoPublicacion): Promise<AmenidadAmen> {
  return normalizarAmenidad(((await rpcAmen("amen_cambiar_publicacion", { p_id_amenidad: idAmenidad, p_estado: estado })) as Crudo) ?? {});
}

export async function crearBloqueoAmen(idEspacio: number, inicio: string, fin: string, motivo: string): Promise<void> {
  await rpcAmen("amen_bloqueo_crear", { p_id_espacio: idEspacio, p_inicio: inicio, p_fin: fin, p_motivo: motivo });
}

export async function quitarBloqueoAmen(id: number): Promise<void> {
  await rpcAmen("amen_bloqueo_quitar", { p_id: id });
}

export async function fetchAmenDisponibilidad(idAmenidad: number, fecha: string, duracionMin: number | null): Promise<DisponibilidadAmen> {
  const d = await rpcAmen("amen_disponibilidad", { p_id_amenidad: idAmenidad, p_fecha: fecha, p_duracion_min: duracionMin });
  return normalizarDisponibilidad((d as Crudo) ?? {});
}

export async function fetchAmenReservas(idProyecto: number, desde: string, hasta: string, verComo?: string | null): Promise<ReservasAmen> {
  const d = await rpcAmen("amen_reservas", conVerComo({ p_id_proyecto: idProyecto, p_desde: desde, p_hasta: hasta }, verComo));
  return normalizarReservas(d as Crudo);
}

/**
 * Detalle de una reserva por id o por folio (la ruta lleva el folio). Si la RPC del ambiente no
 * acepta `p_folio`, se busca el id en la lista de un año alrededor de hoy y se pide por id.
 */
export async function fetchAmenReservaDetalle(
  ref: { id?: number | null; folio?: string | null; idProyecto?: number | null },
  verComo?: string | null,
): Promise<DetalleReservaAmen> {
  const porId = async (id: number) => normalizarDetalle(((await rpcAmen("amen_reserva_detalle", conVerComo({ p_id: id }, verComo))) as Crudo) ?? {});
  if (ref.id) return porId(ref.id);
  try {
    return normalizarDetalle(((await rpcAmen("amen_reserva_detalle", conVerComo({ p_id: null, p_folio: ref.folio }, verComo))) as Crudo) ?? {});
  } catch (e) {
    if (!(e instanceof RpcCondominioPendiente) || !ref.idProyecto || !ref.folio) throw e;
    const hoy = hoyCdmx();
    const lista = await fetchAmenReservas(ref.idProyecto, sumarDias(hoy, -365), sumarDias(hoy, 365), verComo);
    const fila = lista.reservas.find((r) => r.folio === ref.folio);
    if (!fila) throw Object.assign(new Error("no_encontrada"), { code: "P0001", message: "no_encontrada" });
    return porId(fila.id);
  }
}

export async function crearReservaPortal(p: Crudo): Promise<{ reserva: ReservaFila; pago: PagoReserva | null }> {
  const d = ((await rpcAmen("amen_crear_reserva_portal", { p })) as Crudo) ?? {};
  return { reserva: normalizarReserva((d.reserva as Crudo) ?? {}), pago: normalizarPagoReserva(d.pago as Crudo | null) };
}

export async function confirmarSinPagoAmen(id: number, motivo: string): Promise<void> {
  await rpcAmen("amen_confirmar_sin_pago", { p_id: id, p_motivo: motivo });
}

export async function fetchEfectoCancelacionAmen(id: number): Promise<EfectoCancelacionInfo> {
  const d = ((await rpcAmen("amen_efecto_cancelacion", { p_id: id })) as Crudo) ?? {};
  return { efecto: (String(d.efecto ?? "sin_cargo") as EfectoCancelacion), monto: Number(d.monto) || 0, texto: String(d.texto ?? "") };
}

export async function cancelarReservaAmen(id: number, motivo: string): Promise<{ id_cuenta: number | null; requiere_recalculo: boolean }> {
  const d = ((await rpcAmen("amen_cancelar_reserva", { p_id: id, p_motivo: motivo })) as Crudo) ?? {};
  return { id_cuenta: d.id_cuenta == null ? null : Number(d.id_cuenta), requiere_recalculo: d.requiere_recalculo === true };
}

export async function checkinAmen(id: number, equipo: string[]): Promise<void> {
  await rpcAmen("amen_checkin", { p_id: id, p_equipo: equipo });
}

export async function checkoutAmen(id: number, equipoDevuelto: string[], limpiezaOk: boolean, notas: string): Promise<void> {
  await rpcAmen("amen_checkout", { p_id: id, p_equipo_devuelto: equipoDevuelto, p_limpieza_ok: limpiezaOk, p_notas: notas });
}

export async function fetchAmenAgenda(idProyecto: number, fecha: string, verComo?: string | null): Promise<AgendaAmen> {
  const d = await rpcAmen("amen_agenda", conVerComo({ p_id_proyecto: idProyecto, p_fecha: fecha }, verComo));
  return normalizarAgenda((d as Crudo) ?? {});
}

export async function vincularIncidenciaAmen(idReserva: number, idTicket: number): Promise<void> {
  await rpcAmen("amen_vincular_incidencia", { p_id_reserva: idReserva, p_id_ticket: idTicket });
}

export async function fetchAmenIncidencias(idProyecto: number, desde: string, hasta: string, verComo?: string | null): Promise<IncidenciaAmen[]> {
  const d = await rpcAmen("amen_incidencias", conVerComo({ p_id_proyecto: idProyecto, p_desde: desde, p_hasta: hasta }, verComo));
  return ((d as Crudo[]) ?? []).map(normalizarIncidencia);
}

export async function fetchAmenReporte(idProyecto: number, desde: string, hasta: string): Promise<ReporteAmen> {
  return normalizarReporte(((await rpcAmen("amen_reporte", { p_id_proyecto: idProyecto, p_desde: desde, p_hasta: hasta })) as Crudo) ?? {});
}

export async function fetchAmenMetricas(idProyecto: number, desde: string, hasta: string): Promise<MetricasAmen> {
  return normalizarMetricas(((await rpcAmen("amen_metricas", { p_id_proyecto: idProyecto, p_desde: desde, p_hasta: hasta })) as Crudo) ?? {});
}

export async function fetchAmenTitulares(idCuenta: number): Promise<TitularesCuenta> {
  return normalizarTitulares(((await rpcAmen("amen_titulares_cuenta", { p_id_cuenta: idCuenta })) as Crudo) ?? {});
}

/**
 * Edge `admin-amenidades` (staff; gate por permiso del submenú). Sus errores traen
 * `{error: '<codigo>', mensaje: '<texto>'}`: se lanzan con `code` y `mensaje` para mostrar el texto.
 * 503 `amenidades_no_disponible` = la RPC aún no está en el ambiente.
 */
async function edgeAmen<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke("admin-amenidades", { body });
  if (!error) return data as T;
  let cuerpo: { error?: string; mensaje?: string } = {};
  const ctx = (error as { context?: { json?: () => Promise<unknown> } }).context;
  if (ctx?.json) {
    try { cuerpo = ((await ctx.json()) ?? {}) as typeof cuerpo; } catch { /* cuerpo no JSON */ }
  }
  if (cuerpo.error === "amenidades_no_disponible") throw new RpcCondominioPendiente();
  throw Object.assign(new Error(cuerpo.mensaje || cuerpo.error || error.message), { code: cuerpo.error ?? null, mensaje: cuerpo.mensaje ?? null });
}

/**
 * Reaplica los pagos de la cuenta tras desactivar un cargo (cancelación con saldo a favor). La
 * edge responde 409 `sin_cargos_de_reserva` si la cuenta no tiene cargos de reserva: no hay nada
 * que reaplicar y no es un error.
 */
export async function recalcularCuentaAmen(idCuenta: number): Promise<void> {
  try {
    await edgeAmen({ action: "recalcular", id_cuenta: idCuenta });
  } catch (e) {
    if ((e as { code?: string }).code === "sin_cargos_de_reserva") return;
    throw e;
  }
}

/** URLs firmadas (1 h) de fotos, videos y PDF del bucket privado. Sin la edge: `{}` (se ve el ícono). */
export async function fetchUrlsMediaAmen(paths: string[]): Promise<Record<string, string>> {
  if (paths.length === 0) return {};
  try {
    const d = await edgeAmen<{ urls?: Record<string, string | null> }>({ action: "url_media", paths });
    return Object.fromEntries(Object.entries(d?.urls ?? {}).filter((e): e is [string, string] => !!e[1]));
  } catch (e) {
    console.error("[amenidades] url_media", e);
    return {};
  }
}

/**
 * Sube una foto, video o PDF: la edge da la URL firmada de subida (ruta
 * `<id_proyecto>/<id_amenidad|nueva>/<uuid>.<ext>`; jpg, jpeg, png, webp, mp4 o pdf) y el archivo
 * va directo al bucket. Devuelve la ruta y el tipo que decidió la edge.
 */
export async function subirMediaAmen(
  destino: { idAmenidad: number } | { idProyecto: number },
  archivo: File,
): Promise<{ path: string; tipo: string | null }> {
  const d = await edgeAmen<{ path: string; token: string; signed_url?: string; tipo?: string }>({
    action: "subir_media",
    ...("idAmenidad" in destino ? { id_amenidad: destino.idAmenidad } : { id_proyecto: destino.idProyecto }),
    nombre: archivo.name,
  });
  const { error } = await supabase.storage.from("amenidades-media").uploadToSignedUrl(d.path, d.token, archivo, { contentType: archivo.type || undefined });
  if (error) throw error;
  return { path: d.path, tipo: d.tipo ?? null };
}
